import React from 'react';
import { Check, Award, Users, Factory, Sparkles, Target, Eye, Heart } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import SectionTitle from '../components/SectionTitle';
import SectionRenderer from '../components/page-builder/SectionRenderer';
import { stats, aboutFeatures } from '../mock/data';
import { useSettings } from '../context/SettingsContext';
import { getPage } from '../lib/pageDefaults';

export default function About() {
  const { settings } = useSettings();
  const page = getPage(settings?.page_builder, '/tentang') || {};
  const c = page.content || {};
  return (
    <>
      <PageBanner
        title={page.title || "Tentang BelBel (Sidoarjo Powder)"}
        subtitle={page.subtitle || "Supplier bubuk minuman premium dengan komitmen menghadirkan produk berkualitas tinggi untuk pertumbuhan bisnis mitra kami."}
        breadcrumb={['Tentang BelBel']}
      />

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-rose-50 to-yellow-50 p-4">
                <img
                  src={c.profileImage || "https://images.unsplash.com/photo-1633509817627-5a29634475af?w=900&q=80"}
                  alt="BelBel Products"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-japo-yellow rounded-2xl p-6 shadow-xl hidden md:block">
                <div className="text-4xl font-extrabold text-japo-green-dark">{c.badgeValue || '18 Th'}</div>
                <div className="text-sm font-semibold text-gray-800">{c.badgeLabel || 'Tahun Pengalaman'}</div>
              </div>
            </div>
            <div>
              <div className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-3 text-japo-green">
                {c.profileEyebrow || 'Profil Perusahaan'}
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6 tracking-tight leading-tight">
                {c.profileTitle || 'Bubuk Minuman Premium untuk Bisnis Berkualitas'}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                {c.profileBody1 || 'BelBel (Sidoarjo Powder) adalah brand bubuk minuman dengan kualitas premium yang telah dipercaya sejak 2013. BelBel diproduksi menggunakan bahan baku bermutu tinggi dan diproses secara higienis oleh tenaga produksi profesional, sehingga menghasilkan bubuk minuman (powder drink) yang berkualitas.'}
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                {c.profileBody2 || 'Kami berkomitmen untuk selalu melakukan inovasi dalam menciptakan varian rasa yang sesuai dengan selera pasar. Beberapa varian BelBel yang dicintai masyarakat yaitu cappucino, coklat, teh (thai tea, matcha green tea), milkshake, buah-buahan, dan masih banyak lagi.'}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Factory, label: 'Produksi Higienis' },
                  { icon: Award, label: 'Sertifikat Halal' },
                  { icon: Users, label: 'Layanan Responsif' },
                  { icon: Sparkles, label: 'Inovasi Rasa' }
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl bg-gray-50">
                    <div className="w-10 h-10 rounded-lg bg-japo-green flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-semibold text-sm text-gray-800">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Mission */}
      <section id="visi-misi" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionTitle eyebrow="Visi & Misi" title="Komitmen Kami untuk Anda" description="Nilai-nilai yang menjadi fondasi setiap langkah BelBel dalam melayani mitra bisnis." />
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Eye,
                title: c.visiTitle || 'Visi',
                text: c.visiText || 'Menjadi supplier bubuk minuman premium nomor 1 di Indonesia yang dipercaya oleh jutaan pelaku usaha minuman.'
              },
              {
                icon: Target,
                title: c.misiTitle || 'Misi',
                text: c.misiText || 'Menghadirkan produk berkualitas premium dengan harga terjangkau, layanan responsif, dan inovasi rasa yang mengikuti tren pasar.'
              },
              {
                icon: Heart,
                title: c.nilaiTitle || 'Nilai Kami',
                text: c.nilaiText || 'Kejujuran, kualitas premium tanpa kompromi, dan kemitraan jangka panjang yang saling menguntungkan.'
              }
            ].map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-8 card-shadow border border-gray-100">
                <div className="w-14 h-14 rounded-xl bg-japo-yellow flex items-center justify-center mb-5">
                  <v.icon className="w-7 h-7 text-gray-900" />
                </div>
                <h3 className="font-bold text-2xl text-gray-900 mb-3">{v.title}</h3>
                <p className="text-gray-600 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="sertifikasi" className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionTitle eyebrow={c.featuresEyebrow || 'Kualitas & Sertifikasi'} title={c.featuresTitle || 'Kenapa Memilih BelBel?'} description={c.featuresDescription || 'Setiap produk BelBel memenuhi standar kualitas premium dan telah bersertifikat resmi.'} />
          <div className="grid md:grid-cols-3 gap-6">
            {aboutFeatures.map((f) => (
              <div key={f.title} className="bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-japo-green card-shadow">
                <h3 className="font-bold text-xl mb-5 text-japo-green">{f.title}</h3>
                <ul className="space-y-3">
                  {f.items.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-gray-700">
                      <div className="w-6 h-6 rounded-full bg-japo-green flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                      </div>
                      <span className="font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats banner */}
      <section className="py-16 bg-japo-green">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl md:text-5xl font-extrabold text-japo-yellow mb-2">{s.value}</div>
                <div className="text-white/80 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionRenderer sections={page.sections || []} />
    </>
  );
}
